import { match } from 'ts-pattern'

export type FailingFieldProblem = 'MISSING' | 'INVALID'

export type FailingField = {
  name: string
  problem: FailingFieldProblem
}

export type FailingFieldGroup = {
  problem: FailingFieldProblem
  label: string
  fields: string[]
}

export const FAILING_FIELD_PROBLEMS: readonly FailingFieldProblem[] = [
  'MISSING',
  'INVALID'
]

export const FAILING_FIELD_PROBLEM_LABELS: Record<FailingFieldProblem, string> =
  {
    MISSING: 'Missing',
    INVALID: 'Invalid'
  }

export const parseFailingFields = (
  errorMessage: string
): FailingField[] | undefined => {
  const parsed = parseJson(errorMessage)

  const isFailingFieldList =
    Array.isArray(parsed) && parsed.length > 0 && parsed.every(isFailingField)

  return isFailingFieldList ? parsed : undefined
}

export const customerFieldLabel = (fieldName: string): string =>
  match(fieldName)
    .with('legalName', () => 'Company name')
    .with('address', () => 'Address')
    .with('address.line1', () => 'Address line 1')
    .with('address.line2', () => 'Address line 2')
    .with('address.city', () => 'City/town')
    .with('address.postCode', () => 'Postal code')
    .with('address.country', () => 'Country')
    .with('address.state', () => 'State')
    .with('email', () => 'Primary email')
    .otherwise(() => fieldName)

export const failingFieldGroups = (
  fields: FailingField[],
  toLabel: (fieldName: string) => string
): FailingFieldGroup[] =>
  FAILING_FIELD_PROBLEMS.reduce<FailingFieldGroup[]>((groups, problem) => {
    const labels = fields
      .filter(field => field.problem === problem)
      .map(field => toLabel(field.name))

    if (labels.length === 0) {
      return groups
    }

    return [
      ...groups,
      { problem, label: FAILING_FIELD_PROBLEM_LABELS[problem], fields: labels }
    ]
  }, [])

const isFailingField = (value: unknown): value is FailingField => {
  const candidate = value as Partial<FailingField> | null

  return (
    typeof candidate?.name === 'string' &&
    FAILING_FIELD_PROBLEMS.includes(candidate.problem as FailingFieldProblem)
  )
}

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}
